import React, { useState, useEffect, useMemo } from 'react';
import { Newspaper, Calendar, User, Tag, Search, Filter, Loader2, ArrowRight, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { listNews, DEFAULT_BASE_URL } from '../services/api';
import toast from 'react-hot-toast';

const NewsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  const getApiOrigin = () => {
    try {
      const apiBase = DEFAULT_BASE_URL.replace(/\/$/, '');
      const parsed = new URL(apiBase);
      return `${parsed.protocol}//${parsed.host}`;
    } catch {
      return window.location.origin;
    }
  };

  const getNewsImageSrc = (news) => {
    const raw = news?.imageUrl || news?.image || '';
    if (!raw) return '';
    if (raw.startsWith('http') || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
    if (raw.startsWith('/')) return `${getApiOrigin()}${raw}`;
    return raw;
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  // Load news from backend API
  useEffect(() => {
    const loadNews = async () => {
      setLoading(true);
      try {
        const data = await listNews();
        // Sort by date in descending order (newest first)
        const sorted = Array.isArray(data) 
          ? data.sort((a, b) => {
              const dateA = new Date(a.publishedAt || a.createdAt || a.date || 0);
              const dateB = new Date(b.publishedAt || b.createdAt || b.date || 0);
              return dateB - dateA; // Descending order
            })
          : [];
        setNewsData(sorted);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load news:', e);
        }
        toast.error(getText('news.loadingError') || 'Failed to load news. Please try again later.');
        setNewsData([]);
      } finally {
        setLoading(false);
      }
    };

    loadNews();
    const interval = setInterval(loadNews, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Dynamically extract categories from news data
  const categories = useMemo(() => {
    const uniqueCategories = new Set();
    newsData.forEach(news => {
      if (news.category && news.category.trim() !== '') {
        uniqueCategories.add(news.category.trim());
      }
    });
    return ['all', ...Array.from(uniqueCategories).sort()];
  }, [newsData]);

  // Filter news based on category and search term
  const filteredNews = useMemo(() => {
    return newsData.filter(news => {
      const matchesCategory = selectedCategory === 'all' || news.category === selectedCategory;
      const matchesSearch = searchTerm === '' || 
        (news.title && news.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (news.summary && news.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (news.content && news.content.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [newsData, selectedCategory, searchTerm]);

  // Get category translation
  const getCategoryTranslation = (category) => {
    const categoryMap = {
      'Regulatory Updates': getText('news.category.regulatoryUpdates'),
      'Events': getText('news.category.events'),
      'Infrastructure': getText('news.category.infrastructure'),
      'Environmental': getText('news.category.environmental'),
      'Legal Updates': getText('news.category.legalUpdates'),
      'Industry News': getText('news.category.industryNews'),
      'Policy Updates': getText('news.category.policyUpdates'),
      'Uncategorized': getText('news.category.uncategorized')
    };
    return categoryMap[category] || category;
  };

  // Get category color for styling
  const getCategoryColor = (category) => {
    const colorMap = {
      'Regulatory Updates': 'bg-blue-100 text-blue-800 border-blue-200',
      'Events': 'bg-green-100 text-green-800 border-green-200',
      'Infrastructure': 'bg-purple-100 text-purple-800 border-purple-200',
      'Environmental': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Legal Updates': 'bg-red-100 text-red-800 border-red-200',
      'Industry News': 'bg-amber-100 text-amber-800 border-amber-200',
      'Policy Updates': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    };
    return colorMap[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('news.title') || 'News & Updates'}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('news.subtitle') || 'Stay informed with the latest news and updates from IFM'}
            </p>
          </div>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-4 w-full md:w-auto">
              <div className="flex items-center gap-2 text-gray-600">
                <Filter className="h-5 w-5" />
                <span className="text-sm font-medium">{getText('news.filter')}:</span>
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-1 md:flex-none px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white font-medium"
              >
                <option value="all">{getText('news.allCategories')} ({newsData.length})</option>
                {categories.filter(cat => cat !== 'all').map(category => {
                  const count = newsData.filter(n => n.category === category).length;
                  return (
                    <option key={category} value={category}>
                      {getCategoryTranslation(category)} ({count})
                    </option>
                  );
                })}
              </select>
            </div>
            
            <div className="relative w-full md:w-auto">
              <Search className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search news articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full md:w-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* News + Sidebar */}
      <section className="py-16">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {/* Main list */}
            <div className="md:col-span-2 lg:col-span-3">
              {loading ? (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
                    <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
                  </div>
                  <p className="text-gray-600 font-medium">{getText('news.loading')}</p>
                </div>
              ) : filteredNews.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-200">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
                    <Newspaper className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{getText('news.noNewsFound')}</h3>
                  <p className="text-gray-500 mb-4">
                    {searchTerm || selectedCategory !== 'all' 
                      ? getText('events.tryAdjusting')
                      : newsData.length === 0
                      ? getText('news.noNewsAvailable')
                      : getText('news.noArticlesMatch')}
                  </p>
                  {(searchTerm || selectedCategory !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory('all');
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      {getText('news.clearFilters')}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {selectedCategory === 'all' ? getText('news.title') : getCategoryTranslation(selectedCategory)}
                    </h2>
                    <span className="text-sm text-gray-500 font-medium">
                      {filteredNews.length} {filteredNews.length !== 1 ? getText('news.articles') : getText('news.article')}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    {filteredNews.map((news) => (
                      <a 
                        href={`/news/${news._id || news.id}`} 
                        key={news._id || news.id} 
                        className="bg-white rounded-xl shadow-sm border-2 border-gray-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 block group"
                      >
                        <div className="relative h-44 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                          {getNewsImageSrc(news) ? (
                            <img
                              src={getNewsImageSrc(news)}
                              alt={news.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full flex items-center justify-center ${getNewsImageSrc(news) ? 'hidden' : ''}`}>
                            <Newspaper className="h-10 w-10 text-gray-400" />
                          </div>
                        </div>
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${getCategoryColor(news.category || 'Uncategorized')}`}>
                              <Tag className="h-3 w-3 inline mr-1" />
                              {news.category ? getCategoryTranslation(news.category) : getText('news.category.uncategorized')}
                            </span>
                            {news.isBreaking && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-bold animate-pulse">
                                {getText('news.breaking')}
                              </span>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2 leading-tight">
                            {news.title}
                          </h3>
                          <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                            {news.summary}
                          </p>
                          <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-1.5">
                              <User className="h-4 w-4" />
                              <span className="font-medium">{news.author || getText('news.defaultAuthor')}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-4 w-4" />
                              <span>
                                {news.publishedAt 
                                  ? (() => {
                                      try {
                                        const date = new Date(news.publishedAt);
                                        return isNaN(date.getTime()) 
                                          ? getText('news.detail.recently')
                                          : date.toLocaleDateString('en-US', { 
                                              year: 'numeric', 
                                              month: 'short', 
                                              day: 'numeric' 
                                            });
                                      } catch (e) {
                                        return getText('news.detail.recently');
                                      }
                                    })()
                                  : getText('news.detail.recently')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <aside className="md:col-span-1 lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6 sticky top-24">
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
                  <Newspaper className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-bold text-gray-900">Recent News</h2>
                </div>
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-6 w-6 text-gray-400 animate-spin mx-auto" />
                  </div>
                ) : newsData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No recent news
                  </div>
                ) : (
                  <ul className="space-y-4">
                    {newsData.slice(0, 5).map(n => (
                      <li key={n._id || n.id} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                        <div className="mt-2 h-2 w-2 rounded-full bg-primary-600 flex-shrink-0"></div>
                        <a 
                          href={`/news/${n._id || n.id}`} 
                          className="text-sm text-gray-800 hover:text-primary-600 transition-colors line-clamp-2 flex-1 font-medium leading-relaxed"
                        >
                          {n.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
                
                {/* Categories List */}
                {categories.length > 1 && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Tag className="h-5 w-5 text-primary-600" />
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Categories</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {categories.filter(cat => cat !== 'all').map(category => {
                        const count = newsData.filter(n => n.category === category).length;
                        return (
                          <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                              selectedCategory === category
                                ? getCategoryColor(category) + ' ring-2 ring-primary-500'
                                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            {category} ({count})
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

    </div>
  );
};

export default NewsPage;
